import {ErrorMessage, Form, Formik, FormikHelpers} from 'formik'
import _ from 'lodash'
import {FC, useState} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {FormErrorMessage} from '@/components/common/form/FormErrorMessage'
import {FormItem} from '@/components/common/form/FormItem'
import {TransLink} from '@/components/common/TransLink'
import {usePreferences} from '@/components/context/Preferences'
import {TextInput} from '@/components/inputs/TextInput'
import {useAuthContent} from '@/content/user/authentication'
import {Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {REST_METHODS} from '@/utils/api/types'
import {submitWithButtonStatus} from '@/utils/formikHelpers'
import {useRouter} from '@/utils/hooks/useRouter'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {fetch} from '@/utils/sentry-fetch/browser'
import {analytics} from '@/utils/tracking/analytics'
import {AuthActionType, userAlreadyExistsErrorMessage} from '@/utils/user/constants'
import {userSignupSchema} from '@/utils/validations/common/signup'

import {AuthModalLayout} from './AuthModalLayout'

type FormFields = {
  email: string
}

type SignUpModalProps = {
  setActiveAuthActionType: (authActionType: AuthActionType) => void
  setEmail: (email: string) => void
  closeModal: () => void
}

export const SignUpModal: FC<SignUpModalProps> = ({
  setActiveAuthActionType,
  setEmail,
  closeModal,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {
    signUp: {
      title,
      buttonText,
      signInPromoText,
      signInLinkText,
      signUpNote,
      termsLinkUrl,
      privacyPolicyLinkUrl,
    },
    emailLabel,
    emailPlaceholder,
  } = useAuthContent()
  const {currency, origin} = usePreferences()

  const [submitError, setSubmitError] = useState<string | null>(null)
  const router = useRouter()

  const onSubmit = async (values: FormFields, {setStatus}: FormikHelpers<FormFields>) => {
    const {email} = values
    const callbackUrl = decodeURIComponent(_.get(router.query, 'callbackUrl', '') as string)

    if (submitError) {
      setSubmitError(null)
    }
    await submitWithButtonStatus<{id: string}>({
      setStatus,
      onSubmit: async () => {
        analytics.track('Sign up clicked')
        const response = await fetch(routeTo.api.user.signup(), {
          method: REST_METHODS.POST,
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            email,
            language,
            callbackUrl: callbackUrl ? callbackUrl : null,
            currency,
            region: origin,
          }),
        })

        return response
      },
      onSuccess: (user) => {
        analytics.alias(user.id)
        setActiveAuthActionType(AuthActionType.activate)
        setEmail(email)
      },
      onError: (errorKey, message) => {
        const userExistsError = message.includes(userAlreadyExistsErrorMessage)
        setSubmitError(userExistsError ? 'already-existed-user-note' : errorKey)
      },
      errorPath: 'SignUpModal',
      showSuccessStatus: false,
    })
  }

  return (
    <>
      <Formik
        initialValues={{
          email: '',
        }}
        onSubmit={onSubmit}
        validationSchema={userSignupSchema}
      >
        {({submitForm, errors, touched, status}) => (
          <Form>
            <AuthModalLayout
              title={t(title)}
              primaryButton={{
                text: t(buttonText),
                onClick: submitForm,
                status,
                dataTest: 'signUpButton',
              }}
              onCancel={() => closeModal()}
              errorMessage={submitError ?? undefined}
              footer={
                <>
                  <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                    {t(signInPromoText)}
                    <button
                      onClick={() => setActiveAuthActionType(AuthActionType.signIn)}
                      type="button"
                      className="login-button"
                    >
                      <Text
                        variant={TextVariant.text14}
                        element="span"
                        color={theme.palette.text.light}
                        className="underline"
                      >
                        {t(signInLinkText)}
                      </Text>
                    </button>
                  </Text>
                  <div className="note">
                    <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                      <Trans t={t} i18nKey={signUpNote}>
                        text
                        <TransLink href={termsLinkUrl}>link</TransLink>
                        text
                        <TransLink href={privacyPolicyLinkUrl}>link</TransLink>
                      </Trans>
                    </Text>
                  </div>
                </>
              }
            >
              <FormItem label={t(emailLabel)}>
                <ErrorMessage name="email" render={(msg) => <FormErrorMessage message={msg} />} />
                <TextInput
                  name="email"
                  type="email"
                  hasError={!!errors.email && !!touched.email}
                  placeholder={t(emailPlaceholder)}
                />
              </FormItem>
            </AuthModalLayout>
          </Form>
        )}
      </Formik>

      <style jsx>{`
        .note {
          margin-top: ${theme.spacing[2]};
        }

        .login-button {
          margin-left: ${theme.spacing[1]};
        }
      `}</style>
    </>
  )
}
